<template>
  <app-dialog :value="!!message" :max-width="message && message.image ? 980 : 600" blur scrollable @input="onInput">
    <template #title>{{ message ? message.title : "" }}</template>
    <div v-if="message" class="msg-dlg" :class="{ 'msg-dlg--bild': !!message.image }">
      <p class="msg-dlg__date">{{ message.dateText }}</p>
      <!-- Desktop zweispaltig: Bild links, Text rechts (Kundenwunsch 13.10.2026); schmal untereinander -->
      <div class="msg-dlg__cols">
        <figure v-if="message.image" class="msg-dlg__figure">
          <img
            class="msg-dlg__image"
            :src="publicPath + message.image.src"
            :width="message.image.width"
            :height="message.image.height"
            :alt="message.image.alt"
            loading="lazy"
          />
          <figcaption class="msg-dlg__caption">{{ message.image.caption }}</figcaption>
        </figure>
        <div class="msg-dlg__content">
          <p class="msg-dlg__text">{{ message.intro }}</p>
          <ul v-if="message.points && message.points.length" class="msg-dlg__points">
            <li v-for="point in message.points" :key="point" class="msg-dlg__point">
              <app-icon name="check" :size="14" class="msg-dlg__check" />
              <span>{{ point }}</span>
            </li>
          </ul>
          <p v-if="message.outro" class="msg-dlg__text">{{ message.outro }}</p>
          <div v-if="message.contact" class="msg-dlg__contact">
            <p class="msg-dlg__text msg-dlg__text--strong">{{ message.feedback }}</p>
            <a :href="'mailto:' + message.contact.mail" class="msg-dlg__link">
              <app-icon name="mail" :size="16" />{{ message.contact.mail }}
            </a>
            <a :href="message.contact.phoneHref" class="msg-dlg__link">
              <app-icon name="phone" :size="16" />{{ message.contact.phone }}
            </a>
          </div>
        </div>
      </div>
    </div>
    <!-- Schalter und Knopf immer sichtbar (der Inhalt scrollt bei wenig Höhe) -->
    <template #actions>
      <app-switch-row
        v-if="message"
        class="msg-dlg__mute"
        :value="muted"
        label="Beim nächsten Start nicht mehr anzeigen"
        @input="setMuted"
      />
      <app-button kind="primary" @click="close">Verstanden</app-button>
    </template>
  </app-dialog>
</template>

<script>
// Dialog einer Nachricht (Kundenwunsch 13.10.2026: Hinweis auf die neue Version nach dem Login mit Kontakt für Feedback;
// „benachrichtigung kann per switch abgeschaltet werden dass sie nicht nochmal angezeigt wird beim nächsten start“):
// Titel, Datum, Screenshot (falls vorhanden), Text mit Aufzählung, Kontakt (E-Mail und Telefon als Link, Nummer von www.simasoftware.de) und der
// Schalter „Beim nächsten Start nicht mehr anzeigen“ (wirkt sofort, gespeichert im Browser), unten „Verstanden“.
// Hängt einmal in MainLayout; offen ist die Nachricht in messages.open (src/shell/messages.js), geöffnet beim Start
// (showOnStart) oder über die Glocke bzw. das Mehr-Menü.
import { messages, findMessage, isMuted, setMuted, closeMessage } from '../../shell/messages';

export default {
  name: 'AppMessageDialog',
  data() {
    return { state: messages, publicPath: process.env.BASE_URL || '/' };
  },
  computed: {
    message() {
      return this.state.open ? findMessage(this.state.open) : null;
    },
    muted() {
      return !!this.message && this.state.muted && isMuted(this.message.id);
    }
  },
  methods: {
    setMuted(value) {
      if (this.message) {
        setMuted(this.message.id, !!value);
      }
    },
    close() {
      closeMessage();
    },
    onInput(open) {
      if (!open) {
        closeMessage();
      }
    }
  }
};
</script>

<style scoped>
.msg-dlg {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
p.msg-dlg__date {
  margin: -4px 0 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
/* Screenshot oben: ganze Breite, Kante und leichter Schatten wie eine Karte */
/* Spalten: schmal untereinander, ab 900 px Fensterbreite Bild links (etwas breiter) und Text rechts */
.msg-dlg__cols {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.msg-dlg__content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
@media (min-width: 900px) {
  .msg-dlg--bild .msg-dlg__cols {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
    align-items: start;
    gap: 24px;
  }
}
.msg-dlg__figure {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.msg-dlg__image {
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid var(--line);
  border-radius: var(--r-card);
  box-shadow: var(--sh-menu);
}
.msg-dlg__caption {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
  text-align: center;
}
p.msg-dlg__text {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--ink-2);
}
p.msg-dlg__text--strong {
  color: var(--ink);
  font-weight: 500;
}
.msg-dlg__points {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.msg-dlg__point {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
  color: var(--ink);
}
.msg-dlg__check {
  flex: none;
  margin-top: 3px;
  color: var(--green-dot);
}
/* Kontakt als abgesetzter Block */
.msg-dlg__contact {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 12px;
  border-radius: var(--r-ctl);
  background: var(--band);
}
.msg-dlg__link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--link);
  text-decoration: none;
}
.msg-dlg__link:hover {
  text-decoration: underline;
}
.msg-dlg__link:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
  border-radius: 2px;
}
/* Schalter links in der Aktionszeile, Knopf rechts; schmal untereinander */
.msg-dlg__mute {
  flex: 1 1 260px;
  min-width: 0;
  min-height: 0;
  padding: 0;
  border: 0;
}
/* Schalter direkt hinter dem Text statt rechtsbündig (Kundenwunsch 13.10.2026); der Knopf bleibt rechts */
.msg-dlg__mute ::v-deep .app-sw-row__label {
  flex: 0 1 auto;
}
</style>
